import { CertificateStatus } from '@repo/types'

/**
 * The four in-flight transfer states, collapsed to ONE user-facing label.
 *
 * VERA-304 — the chip used to print the raw enum, so a certificate mid-gift read
 * "Transfer_waiting_confirmation": backend vocabulary, an underscore, and a distinction
 * the owner cannot act on. `transfer_initiated` vs `transfer_waiting_confirmation` vs
 * `waiting_accept` vs `cancel_request` differ only in WHICH internal step the state
 * machine is on; to a person the fact is the same one — "this is being transferred."
 *
 * The card already says which transfer it is and what to do about it ("Gift sent" /
 * "Gift received" + the person + the action), so the chip repeating a sub-state adds no
 * information and leaks the schema. Sub-state stays backend-only: the simulator, the
 * API and the DB still distinguish all four.
 */
const TRANSFER_STATUSES = new Set<string>([
  CertificateStatus.TransferInitiated,
  CertificateStatus.TransferWaitingConfirmation,
  CertificateStatus.WaitingAccept,
  CertificateStatus.CancelRequest,
])

/** Statuses whose label isn't just the capitalised enum. */
const EXPLICIT_LABELS: Record<string, string> = {
  [CertificateStatus.Created]: 'Created',
  [CertificateStatus.Inactive]: 'Inactive',
  [CertificateStatus.Active]: 'Active',
  [CertificateStatus.Lost]: 'Lost',
  [CertificateStatus.Stolen]: 'Stolen',
}

/**
 * Raw certificate status -> the label a person should read.
 *
 * Single source: `StatusBadge` calls this whenever a caller hasn't authored its own
 * label, so every surface (the certificates list card, the certificate page, the
 * pending-transfer card) changes together and none can drift back to the raw enum.
 */
export const getCertificateStatusLabel = (status?: string | null): string => {
  if (!status) return ''
  if (TRANSFER_STATUSES.has(status)) return 'Transfer'
  if (EXPLICIT_LABELS[status]) return EXPLICIT_LABELS[status]

  // Unknown / future status: never show an underscore. Title-case the segments so a
  // new backend state degrades to something readable rather than shipping schema.
  return status
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Raw certificate status -> the `docs/glossary.json` id that DEFINES it.
 *
 * It lives beside `getCertificateStatusLabel` because it has to make the same two
 * decisions that function makes, and a second copy of them would drift:
 *
 *  - the FOUR in-flight transfer statuses collapse to ONE term, exactly as they
 *    collapse to one label. A `certificate-status-${status}` template would ask for
 *    `certificate-status-transfer_waiting_confirmation`, which resolves to nothing —
 *    so the badge showing "Transfer" would carry no definition of "Transfer";
 *  - `lost` and `stolen` do NOT collapse. They are separate labels and separate
 *    entries, because the glossary's own `stolen` entry turns on the distinction
 *    ("Lost invites a finder to return it; stolen tells a prospective buyer…").
 *
 * An explicit map rather than a template for the same reason `EXPLICIT_LABELS` is
 * one: the id space is authored in `docs/glossary.json`, not derived from the enum,
 * so nothing guarantees a new backend status has an entry. `created` is the live
 * proof — it has a label and no entry, and returns `undefined` here rather than a
 * plausible-looking id that resolves to nothing.
 *
 * Returning `undefined` is a complete answer, not a fallback: `TermInfo` renders
 * NOTHING for an id it cannot resolve, so an unmapped status simply has no `(i)`.
 */
const STATUS_TERM_IDS: Record<string, string> = {
  [CertificateStatus.Inactive]: 'certificate-status-inactive',
  [CertificateStatus.Active]: 'certificate-status-active',
  [CertificateStatus.Lost]: 'certificate-status-lost',
  [CertificateStatus.Stolen]: 'certificate-status-stolen',
}

export const getCertificateStatusTermId = (status?: string | null): string | undefined => {
  if (!status) return undefined
  if (TRANSFER_STATUSES.has(status)) return 'certificate-status-transfer'
  return STATUS_TERM_IDS[status]
}
